import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { CustomEvent } from "schemas/functions";
import { INSTAGRAM_USERNAME_URL } from "schemas/social";

import StyledVideo from "components/Styled/Video";
import {
  IG_TAGGING_PREVIEW,
  IG_TAGGING_TUTORIAL_VIDEO_LINK,
} from "constants/influencer";
import {
  isValidUsername,
  sanitizeInput,
} from "features/Auth/Onboarding/AboutForm/helpers";
import { fetcherAuth } from "utils/api";

import InstagramConnect from "./InstagramConnect";

const styles = {
  textField: {
    borderRadius: 4,
    "& fieldset.MuiOutlinedInput-notchedOutline": {
      borderColor: "black",
    },
    "& .MuiInputBase-root": { backgroundColor: "white" },
    "& .MuiFormHelperText-root": { color: "error.main" },
    minWidth: "300px",
  },
  linkAdornment: {
    "& .MuiTypography-root": {
      color: grey[900],
      fontSize: { xs: 12, md: 14 },
    },
  },
  addButton: {
    height: "52px",
  },
  link: {
    color: "primary.main",
    textDecoration: "none",
    fontWeight: 600,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  card: {
    borderRadius: "8px",
    border: `1px solid ${grey[300]}`,
    px: 2,
    py: 1.5,
  },
  removeButton: {
    border: `1px solid ${grey[300]}`,
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "none",
    px: 2,
  },
  continue: {
    px: 10,
    height: "52px",
    mt: 6,
  },
};

export default function FindCreator() {
  const [addedUsernames, setAddedUsernames] = useState<string[]>([]);
  const [username, setUsername] = useState<string>("");
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );
  const { discoverTab } = useContext(DiscoverViewContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();

  const [addUsernameLoading, setAddUsernameLoading] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedInput = sanitizeInput(value.trim());
    setUsername(sanitizedInput);

    if (sanitizedInput && !isValidUsername(sanitizedInput)) {
      setValidationError(
        "Must contain only letters, numbers, underscores, hyphens or periods",
      );
    } else {
      setValidationError(undefined);
    }
  };

  const addUsername = async (e: CustomEvent) => {
    e.preventDefault();
    if (validationError) {
      return;
    }

    if (!username || !currentOrg?.id) return;
    setAddUsernameLoading(true);

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/tracked-creators`,
        "POST",
        {},
        {
          igUsername: username,
        },
      );

      setAddedUsernames((prev) => [...prev, username]);
      setUsername("");

      setAlert(
        <>
          We've started scanning {username}. This could take a few hours. Visit
          your{" "}
          <Link
            component={RouterLink}
            to={`/dashboard/instagram/track`}
            sx={{
              color: "white",
              textDecoration: "underline",
            }}
          >
            Tracking
          </Link>{" "}
          to follow along.
        </>,
        "success",
      );
    } catch (error: any) {
      setErrorAlert(error);
    } finally {
      setAddUsernameLoading(false);
    }
  };

  const removeUsername = async (addedUsername: string) => {
    // optimistic update!
    setAddedUsernames((prev) =>
      prev.filter((username) => username !== addedUsername),
    );

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/tracked-creators/username/${addedUsername}`,
        "DELETE",
        {},
      );
    } catch (error: any) {
      setErrorAlert(error);
    }
  };

  return (
    <>
      <Grid container justifyContent={"center"}>
        <Grid item sx={{ width: 500, mt: { xs: 2, md: 0 } }}>
          <StyledVideo
            image={IG_TAGGING_PREVIEW}
            videoPreview={IG_TAGGING_PREVIEW}
            link={IG_TAGGING_TUTORIAL_VIDEO_LINK}
            title={"How to Find Brands to Pitch through Instagram"}
          />{" "}
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ p: 2 }}>
        <Typography variant="h2" sx={{ textAlign: "center" }} paragraph>
          Find{" "}
          <Box component="span" sx={{ color: "purple.dark" }}>
            creators
          </Box>{" "}
          you admire 💭
        </Typography>
        <Typography sx={{ textAlign: "center", mb: 2 }}>
          We'll uncover the brands they're collaborating with — so you can pitch
          them too.
        </Typography>
      </Grid>
      <Grid item xs={12} container justifyContent="center">
        <Grid
          component="form"
          onSubmit={addUsername}
          item
          xs={12}
          sm={8}
          md={discoverTab !== null ? 12 : 8}
          lg={6}
          container
          gap={1}
          justifyContent="center"
        >
          <Grid item xs>
            <TextField
              sx={styles.textField}
              fullWidth
              placeholder="username"
              value={username}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    sx={styles.linkAdornment}
                    disablePointerEvents
                    position="start"
                  >
                    {INSTAGRAM_USERNAME_URL}
                  </InputAdornment>
                ),
              }}
              onChange={handleUsernameChange}
              error={!!validationError}
              helperText={validationError}
            />
          </Grid>
          <Grid item xs="auto">
            <LoadingButton
              size="large"
              sx={styles.addButton}
              loading={addUsernameLoading}
              variant="contained"
              type="submit"
            >
              <Box
                component="span"
                className="fa-regular fa-plus"
                sx={{ mr: 1 }}
              />
              Add Creator
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
      {addedUsernames.length > 0 && (
        <Grid item container sx={{ mt: 4 }} xs={12} justifyContent="center">
          <Grid
            xs={10}
            item
            container
            justifyContent="center"
            alignItems="center"
            gap={2}
          >
            {addedUsernames.map((addedUsername, i) => (
              <Grid item xs="auto" key={i}>
                <Box
                  sx={styles.card}
                  key={addedUsername}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Typography>{addedUsername}</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={styles.removeButton}
                    color="inherit"
                    onClick={() => removeUsername(addedUsername)}
                  >
                    Remove
                  </Button>
                </Box>
              </Grid>
            ))}
            <Grid item xs="auto">
              <Button
                component={RouterLink}
                to={"/dashboard/instagram/track"}
                sx={{ textTransform: "none" }}
                color="info"
                size="small"
              >
                👉 View Brands
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid item container xs={12} justifyContent="center" sx={{ mb: 10 }}>
        <Grid
          container
          item
          xs={12}
          sm={8}
          md={discoverTab !== null ? 12 : 8}
          lg={8}
          alignItems="center"
          justifyContent={"space-between"}
          gap={4}
          sx={{
            backgroundColor: "purple.light",
            borderRadius: 4,
            p: 3,
            mt: 5,
          }}
        >
          <Grid item xs={12} md>
            <Typography
              gutterBottom
              sx={{
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              <Box component="span" className="fa-brands fa-instagram" />{" "}
              <Box component={"span"} sx={{ color: "black" }}>
                Prefer adding via Instagram?
              </Box>
            </Typography>

            <Typography sx={{ fontSize: 12 }}>
              Tag{" "}
              <Box
                component="a"
                href="https://instagram.com/onbento"
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.link}
              >
                @onbento
              </Box>{" "}
              in a creator's post and we'll add the brands they've tagged to
              your list so you can pitch them.
            </Typography>
          </Grid>
          <Grid item xs={12} md="auto" container justifyContent="center">
            <Button
              variant="outlined"
              sx={{
                px: 2,
                py: 1,
                textTransform: "none",
                color: "purple.dark",
                borderColor: "purple.dark",
              }}
              onClick={() => setEditDialogOpen(true)}
            >
              Connect Instagram
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <InstagramConnect
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
      />
    </>
  );
}
